import {
  type Menu,
  menu,
  type MenuConfig,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { signal } from '@preact/signals-core';
import { Text } from '@blocksuite/store';

import type { RelationRawValue } from './types.js';
import { dedupeRefs } from './types.js';

interface PopRowPickerArgs {
  anchor: HTMLElement;
  targetDb: DatabaseBlockModel;
  targetDocId: string;
  currentValue: RelationRawValue;
  singleRef?: boolean;
  onChange: (next: RelationRawValue) => void;
  onComplete?: () => void;
}

const rowTitleOf = (db: DatabaseBlockModel, rowId: string): string => {
  const row = db.store.getBlock(rowId)?.model;
  const text = row?.text?.toString().trim();
  return text && text.length > 0 ? text : 'Untitled row';
};

export const popRowPicker = ({
  anchor,
  targetDb,
  targetDocId,
  currentValue,
  singleRef,
  onChange,
  onComplete,
}: PopRowPickerArgs) => {
  // Live selection state: the multi-pick menu stays open, so every pick
  // must build on the previous one instead of the value captured at open
  // time. Reading `current.value` inside the item functions also makes the
  // menu re-render (and checkmarks update) after each pick.
  const current = signal<RelationRawValue>(currentValue);

  const selectedKeys = () =>
    new Set(current.value.map(ref => `${ref.docId}::${ref.blockId}`));

  const isSelectedRow = (rowId: string) =>
    current.value.some(
      ref => ref.docId === targetDocId && ref.blockId === rowId
    );

  const pickRow = (rowId: string) => {
    if (singleRef) {
      const isSelected = isSelectedRow(rowId);
      const next: RelationRawValue = isSelected
        ? []
        : [{ _v: 1 as const, docId: targetDocId, blockId: rowId }];
      current.value = next;
      onChange(next);
      return;
    }
    if (isSelectedRow(rowId)) {
      const next = current.value.filter(
        ref => !(ref.docId === targetDocId && ref.blockId === rowId)
      );
      current.value = next;
      onChange(next);
    } else {
      const next = dedupeRefs([
        ...current.value,
        { _v: 1 as const, docId: targetDocId, blockId: rowId },
      ]);
      current.value = next;
      onChange(next);
    }
  };

  const rows = targetDb.children.map(child => ({
    rowId: child.id,
    title: rowTitleOf(targetDb, child.id),
  }));

  const createRow = (title: string) => {
    const trimmed = title.trim();
    const newRowId = targetDb.store.addBlock(
      'affine:paragraph',
      trimmed.length > 0 ? { text: new Text(trimmed) } : {},
      targetDb.id,
      targetDb.children.length
    );
    if (singleRef) {
      const next: RelationRawValue = [
        { _v: 1 as const, docId: targetDocId, blockId: newRowId },
      ];
      current.value = next;
      onChange(next);
    } else {
      const next = dedupeRefs([
        ...current.value,
        { _v: 1 as const, docId: targetDocId, blockId: newRowId },
      ]);
      current.value = next;
      onChange(next);
    }
  };

  const items: MenuConfig[] = [];

  if (rows.length > 0) {
    items.push(
      ...rows.map(({ rowId, title }) => {
        const key = `${targetDocId}::${rowId}`;
        return (currentMenu: Menu) => {
          const isSelected = selectedKeys().has(key);
          return menu.action({
            name: title,
            isSelected,
            // Keep the menu open after each pick so several rows can be
            // selected/unselected in one session; the user closes it
            // explicitly (outside click, Escape, or the × button).
            closeOnSelect: singleRef ? undefined : false,
            select: () => pickRow(rowId),
          })(currentMenu);
        };
      })
    );
  }

  // Always offer to create a new row in the target database.
  // When the user has typed (or pasted) text into the search box, use it as
  // the title of the newly created row.
  items.push(currentMenu => {
    const typed = currentMenu.searchName$.value.trim();
    return menu.action({
      name:
        typed.length > 0 ? `+ Create new row "${typed}"` : '+ Create new row',
      closeOnSelect: singleRef ? undefined : false,
      select: () => createRow(typed),
    })(currentMenu);
  });

  const handle = popMenu(popupTargetFromElement(anchor), {
    options: {
      title: { text: singleRef ? 'Pick one row' : 'Pick rows' },
      search: { placeholder: 'Search rows…' },
      items,
      onComplete,
    },
  });
  return handle;
};
